/**
 * Storage Service for Poker Reborn
 *
 * Handles persistent local storage with type safety.
 *
 * @see docs/screens/settings/README.md - Feature documentation
 */

import { EventEmitter } from './EventEmitter';
import { log } from '@/utils/log';

// =============================================================================
// TYPES
// =============================================================================

/** Storage keys */
export type StorageKey =
  | 'settings.sound.master'
  | 'settings.sound.sfx'
  | 'settings.sound.music'
  | 'settings.sound.muted'
  | 'settings.graphics.quality'
  | 'settings.game.autoMuck'
  | 'settings.game.fourColorDeck'
  | 'settings.game.showHandStrength'
  | 'auth.username'
  | 'auth.token'
  | 'user.lastTableId'
  | 'user.preferredSeat'
  | 'user.avatarId';

/** Storage value types */
export interface StorageSchema {
  'settings.sound.master': number;
  'settings.sound.sfx': number;
  'settings.sound.music': number;
  'settings.sound.muted': boolean;
  'settings.graphics.quality': 'low' | 'medium' | 'high';
  'settings.game.autoMuck': boolean;
  'settings.game.fourColorDeck': boolean;
  'settings.game.showHandStrength': boolean;
  'auth.username': string;
  'auth.token': string;
  'user.lastTableId': string;
  'user.preferredSeat': number;
  'user.avatarId': number;
}

/** Default values */
const DEFAULTS: Partial<StorageSchema> = {
  'settings.sound.master': 1.0,
  'settings.sound.sfx': 1.0,
  'settings.sound.music': 0.5,
  'settings.sound.muted': false,
  'settings.graphics.quality': 'high',
  'settings.game.autoMuck': true,
  'settings.game.fourColorDeck': false,
  'settings.game.showHandStrength': true,
};

/** Storage events */
export interface StorageEvents {
  /** Value changed */
  change: { key: StorageKey; value: unknown; previousValue: unknown };

  /** Storage cleared */
  clear: void;
}

// =============================================================================
// STORAGE SERVICE
// =============================================================================

export class StorageService extends EventEmitter<StorageEvents> {
  private static instance: StorageService | null = null;

  private prefix = 'poker_reborn_';
  private cache: Map<string, unknown> = new Map();
  private available = false;

  private constructor() {
    super();
    this.checkAvailability();
    this.loadCache();
  }

  /** Get singleton instance */
  static getInstance(): StorageService {
    if (!StorageService.instance) {
      StorageService.instance = new StorageService();
    }
    return StorageService.instance;
  }

  /**
   * Get a value from storage
   */
  get<K extends StorageKey>(key: K): StorageSchema[K] | undefined {
    // Check cache first
    if (this.cache.has(key)) {
      return this.cache.get(key) as StorageSchema[K];
    }

    // Return default if available
    if (key in DEFAULTS) {
      return DEFAULTS[key as keyof typeof DEFAULTS] as StorageSchema[K];
    }

    return undefined;
  }

  /**
   * Get a value with a fallback default
   */
  getOrDefault<K extends StorageKey>(key: K, defaultValue: StorageSchema[K]): StorageSchema[K] {
    const value = this.get(key);
    return value !== undefined ? value : defaultValue;
  }

  /**
   * Set a value in storage
   */
  set<K extends StorageKey>(key: K, value: StorageSchema[K]): void {
    const previousValue = this.get(key);

    // Update cache
    this.cache.set(key, value);

    // Persist to localStorage
    if (this.available) {
      try {
        const serialized = JSON.stringify(value);
        localStorage.setItem(this.prefix + key, serialized);
      } catch (error) {
        log.storage.warning`Failed to save to localStorage: ${error}`;
      }
    }

    // Emit change event
    this.emit('change', { key, value, previousValue });
  }

  /**
   * Remove a value from storage
   */
  remove(key: StorageKey): void {
    const previousValue = this.get(key);

    this.cache.delete(key);

    if (this.available) {
      try {
        localStorage.removeItem(this.prefix + key);
      } catch (error) {
        log.storage.warning`Failed to remove from localStorage: ${error}`;
      }
    }

    this.emit('change', { key, value: undefined, previousValue });
  }

  /**
   * Check if a key exists in storage
   */
  has(key: StorageKey): boolean {
    return this.cache.has(key);
  }

  /**
   * Clear all storage
   */
  clear(): void {
    this.cache.clear();

    if (this.available) {
      try {
        // Only clear our prefixed keys
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith(this.prefix)) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach((key) => { localStorage.removeItem(key); });
      } catch (error) {
        log.storage.warning`Failed to clear localStorage: ${error}`;
      }
    }

    this.emit('clear', undefined);
  }

  /**
   * Check if localStorage is available
   */
  isAvailable(): boolean {
    return this.available;
  }

  // ===========================================================================
  // CONVENIENCE METHODS
  // ===========================================================================

  /** Get all sound settings */
  getSoundSettings() {
    return {
      master: this.getOrDefault('settings.sound.master', 1.0),
      sfx: this.getOrDefault('settings.sound.sfx', 1.0),
      music: this.getOrDefault('settings.sound.music', 0.5),
      muted: this.getOrDefault('settings.sound.muted', false),
    };
  }

  /** Set sound settings */
  setSoundSettings(settings: Partial<ReturnType<typeof this.getSoundSettings>>) {
    if (settings.master !== undefined) this.set('settings.sound.master', settings.master);
    if (settings.sfx !== undefined) this.set('settings.sound.sfx', settings.sfx);
    if (settings.music !== undefined) this.set('settings.sound.music', settings.music);
    if (settings.muted !== undefined) this.set('settings.sound.muted', settings.muted);
  }

  /** Get all game settings */
  getGameSettings() {
    return {
      autoMuck: this.getOrDefault('settings.game.autoMuck', true),
      fourColorDeck: this.getOrDefault('settings.game.fourColorDeck', false),
      showHandStrength: this.getOrDefault('settings.game.showHandStrength', true),
    };
  }

  /** Set game settings */
  setGameSettings(settings: Partial<ReturnType<typeof this.getGameSettings>>) {
    if (settings.autoMuck !== undefined) this.set('settings.game.autoMuck', settings.autoMuck);
    if (settings.fourColorDeck !== undefined) this.set('settings.game.fourColorDeck', settings.fourColorDeck);
    if (settings.showHandStrength !== undefined) this.set('settings.game.showHandStrength', settings.showHandStrength);
  }

  /** Get auth credentials */
  getAuth() {
    return {
      username: this.get('auth.username'),
      token: this.get('auth.token'),
    };
  }

  /** Set auth credentials */
  setAuth(username: string, token: string) {
    this.set('auth.username', username);
    this.set('auth.token', token);
  }

  /** Clear auth credentials */
  clearAuth() {
    this.remove('auth.username');
    this.remove('auth.token');
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  private checkAvailability(): void {
    try {
      const testKey = '__storage_test__';
      localStorage.setItem(testKey, testKey);
      localStorage.removeItem(testKey);
      this.available = true;
    } catch {
      this.available = false;
      log.storage.warning`localStorage is not available`;
    }
  }

  private loadCache(): void {
    if (!this.available) return;

    try {
      for (let i = 0; i < localStorage.length; i++) {
        const fullKey = localStorage.key(i);
        if (fullKey && fullKey.startsWith(this.prefix)) {
          const key = fullKey.slice(this.prefix.length);
          const value = localStorage.getItem(fullKey);
          if (value) {
            try {
              this.cache.set(key, JSON.parse(value));
            } catch {
              // Invalid JSON, skip
            }
          }
        }
      }
    } catch (error) {
      log.storage.warning`Failed to load from localStorage: ${error}`;
    }
  }
}

/** Singleton storage service instance */
export const storageService = StorageService.getInstance();
